import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../apiClientContext";
import { useKeepAdjacentData } from "../queryPlaceholders";
import {
    addWorkItem,
    approveWorkItem,
    askQuestion,
    attachWorkItemQueue,
    closeConversation,
    completeWorkItem,
    createWorkflow,
    detachWorkItemQueue,
    editNote,
    fetchTaskTypes,
    fetchWorkflow,
    fetchWorkflowKinds,
    listChildWorkflows,
    listWorkflowsByEntity,
    recordWorkItemView,
    rejectWorkItem,
    searchWorkflows,
    setNotePinned,
    snoozeWorkItem,
    spawnChildWorkflow,
} from "./api";
import type {
    AddWorkItemInput,
    ApproveWorkItemInput,
    AskQuestionInput,
    CreateWorkflowInput,
    RejectWorkItemInput,
    SnoozeWorkItemInput,
    SpawnChildWorkflowInput,
} from "./api";
import type { CloseConversationResponse, WorkItem, Workflow } from "./types";

const workflowKeys = {
    all: ["workflows"] as const,
    detail: (id: string, entityType: string, entityId: string) =>
        ["workflows", "detail", id, entityType, entityId] as const,
    byEntity: (entityType: string, entityId: string) =>
        ["workflows", "byEntity", entityType, entityId] as const,
    search: (query: string) => ["workflows", "search", query] as const,
    kinds: () => ["workflows", "kinds"] as const,
    taskTypes: () => ["workflows", "task-types"] as const,
    children: (workflowId: string, workItemId: string, entityType: string, entityId: string) =>
        ["workflows", "children", workflowId, workItemId, entityType, entityId] as const,
};

// Queue-list indicator badges live behind ["queues", "tallies", <ids>].
// Any mutation that adds, completes, re-routes, or re-due-dates a work
// item shifts Open/Pending for the queues that contain it, so the badges
// must invalidate on every such mutation. Declared here (rather than
// re-imported from ../queues/hooks) to keep the dependency direction
// one-way: queues -> workflows is the established arrow and we don't
// want to flip it just to share a key constant.
const QUEUE_TALLIES_KEY = ["queues", "tallies"] as const;

// The taskbar App Badge reads the caller's outstanding count from
// ["workitems","mine"] (see workitems/hooks.ts and app-badge/AppBadgeBridge).
// Any mutation that changes the caller's not-completed set must invalidate
// this key so the badge updates from the user's OWN action — relying on the
// SignalR WorkItemCompleted echo (useQueueRealtime) to refresh it is fragile:
// the event can be delayed, dropped, or not delivered back to the actor, which
// leaves the badge stuck until a focus/reopen refetch. Same one-way-dependency
// rationale as QUEUE_TALLIES_KEY above: declared here, not imported from
// ../workitems, to keep the workflows -> workitems arrow from flipping.
const WORK_ITEMS_MINE_KEY = ["workitems", "mine"] as const;

export function useWorkflowKinds() {
    const client = useApiClient();
    return useQuery({
        queryKey: workflowKeys.kinds(),
        queryFn: () => fetchWorkflowKinds(client),
        // Kinds are baked into the contracts package; the registry rarely
        // changes during a session and never silently. Keeping the cache
        // generous (an hour, GC-stable) avoids per-render refetches in
        // dropdown-heavy admin pages.
        staleTime: 60 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useTaskTypes() {
    const client = useApiClient();
    return useQuery({
        queryKey: workflowKeys.taskTypes(),
        queryFn: () => fetchTaskTypes(client),
        staleTime: 60 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useWorkflow(id: string, entityType: string, entityId: string) {
    const client = useApiClient();
    const enabled = id.length > 0 && entityType.length > 0 && entityId.length > 0;
    const queryKey = workflowKeys.detail(id, entityType, entityId);
    // Switching between two workflows (or two threads) keeps the previous
    // one on screen, dimmed, until the next arrives — never a skeleton.
    const placeholder = useKeepAdjacentData(queryKey, enabled);
    return useQuery({
        queryKey,
        queryFn: () => fetchWorkflow(client, id, entityType, entityId),
        enabled,
        // Realtime patches and invalidation keep the detail live; the
        // window only bounds how long a remount trusts the cache.
        staleTime: 30_000,
        ...placeholder,
    });
}

export function useWorkflowsByEntity(entityType: string, entityId: string) {
    const client = useApiClient();
    return useQuery({
        queryKey: workflowKeys.byEntity(entityType, entityId),
        queryFn: () => listWorkflowsByEntity(client, entityType, entityId),
        enabled: entityType.length > 0 && entityId.length > 0,
    });
}

export interface UseAddWorkItemParams {
    readonly workflowId: string;
    readonly entityType: string;
    readonly entityId: string;
}

export function useAddWorkItem({ workflowId, entityType, entityId }: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: (input: Omit<AddWorkItemInput, "workflowId" | "entityType" | "entityId">) =>
            addWorkItem(client, { ...input, workflowId, entityType, entityId }),
        onSuccess: (newItem: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: [...prev.workItems, newItem],
                };
            });
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
            // A new item can land in a queue the caller belongs to, raising
            // their outstanding count — refresh the badge. Mirrors the
            // unconditional "mine" invalidation in useQueueRealtime.handleAdded.
            void queryClient.invalidateQueries({ queryKey: WORK_ITEMS_MINE_KEY });
        },
    });
}

export function useCompleteWorkItem({ workflowId, entityType, entityId }: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: (workItemId: string) =>
            completeWorkItem(client, workflowId, workItemId, entityType, entityId),
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
            // Completing removes the item from the caller's outstanding set —
            // decrement the taskbar badge without waiting on the realtime echo.
            void queryClient.invalidateQueries({ queryKey: WORK_ITEMS_MINE_KEY });
        },
    });
}

/**
 * Push the due date of an open task forward (snooze). Caller computes
 * the absolute ISO target — quick-chip presses add minutes to
 * `Date.now()`, the "Custom…" panel resolves the datetime input — and
 * the server re-validates against its own clock so client skew can't
 * land a snooze in the past.
 *
 * Cache strategy mirrors `useCompleteWorkItem`: setQueryData patch on
 * the detail row (DueDate is the only field that changes) plus a
 * trailing invalidate so the 15s tally refetch converges on the
 * server's truth; QUEUE_TALLIES_KEY also invalidates because a snooze
 * can flip an item between the Open and Pending buckets.
 */
export function useSnoozeWorkItem({ workflowId, entityType, entityId }: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: (input: Omit<SnoozeWorkItemInput, "workflowId" | "entityType" | "entityId">) =>
            snoozeWorkItem(client, {
                workflowId,
                entityType,
                entityId,
                workItemId: input.workItemId,
                newDueDate: input.newDueDate,
            }),
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

/**
 * Records that the user opened a task/note for viewing. Fire-and-forget
 * read-path telemetry: a view changes no shared state, so there is no
 * cache patch or invalidation, and `onError` is intentionally a no-op so
 * a failed (or rejected) view record never surfaces to the user. The
 * caller is responsible for deduplicating trivial repeats before calling
 * `mutate`.
 */
export function useRecordWorkItemView({ workflowId, entityType, entityId }: UseAddWorkItemParams) {
    const client = useApiClient();

    return useMutation({
        mutationFn: (workItemId: string) =>
            recordWorkItemView(client, { workflowId, workItemId, entityType, entityId }),
        onError: () => {
            // Swallow: view tracking is best-effort and must not disrupt
            // the UI on a ledger/network hiccup.
        },
    });
}

/**
 * Bulk-closes every open message in a workflow ("Close conversation").
 * The server returns the id list of items it actually closed (skipping
 * anything that raced into a completed state), so we reconcile the
 * detail cache by stamping a synthetic completedAt onto exactly those
 * items rather than refetching the workflow. The follow-up invalidate
 * still fires to converge on the server's truth — useful when racing
 * replies leave the cached `completedBy` slightly behind.
 */
export function useCloseConversation({ workflowId, entityType, entityId }: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: () => closeConversation(client, workflowId, entityType, entityId),
        onSuccess: (result: CloseConversationResponse) => {
            const closed = new Set(result.closedWorkItemIds);
            if (closed.size > 0) {
                const nowIso = new Date().toISOString();
                queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                    if (prev === undefined) {
                        return undefined;
                    }
                    return {
                        ...prev,
                        workItems: prev.workItems.map((item) =>
                            closed.has(item.id) && item.completedAt === null
                                ? { ...item, completedAt: nowIso }
                                : item,
                        ),
                    };
                });
            }
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
            // Closing settles the caller's open messages — refresh the badge.
            void queryClient.invalidateQueries({ queryKey: WORK_ITEMS_MINE_KEY });
        },
    });
}

/**
 * Pins or unpins a note. Pinned notes float to the top of the task/note
 * rail. The mutation patches the detail cache in place (notes never
 * change lifecycle on a pin, so a refetch would be wasted) and converges
 * on the server's truth via the trailing invalidate.
 */
export function usePinNote({ workflowId, entityType, entityId }: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: ({ workItemId, pinned }: { workItemId: string; pinned: boolean }) =>
            setNotePinned(client, workflowId, workItemId, entityType, entityId, pinned),
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
        },
    });
}

/**
 * Edits a note's body text. Notes never change lifecycle on a body edit,
 * so the mutation patches the detail cache in place (like usePinNote)
 * and converges on the server's truth via the trailing invalidate. The
 * SignalR `WorkItemEdited` echo refreshes other connected viewers; this
 * hook keeps the editor's own view immediate without waiting on it.
 */
export function useEditNote({ workflowId, entityType, entityId }: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: ({ workItemId, body }: { workItemId: string; body: string }) =>
            editNote(client, { workflowId, workItemId, entityType, entityId, body }),
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
        },
    });
}

export function useApproveWorkItem({
    workflowId,
    entityType,
    entityId,
}: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: (input: Omit<ApproveWorkItemInput, "workflowId" | "entityType" | "entityId">) =>
            approveWorkItem(client, {
                workflowId,
                entityType,
                entityId,
                workItemId: input.workItemId,
            }),
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) return undefined;
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

export function useRejectWorkItem({
    workflowId,
    entityType,
    entityId,
}: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: (input: Omit<RejectWorkItemInput, "workflowId" | "entityType" | "entityId">) =>
            rejectWorkItem(client, {
                workflowId,
                entityType,
                entityId,
                workItemId: input.workItemId,
                comment: input.comment,
            }),
        // Rejection swaps in the reopened item *and* appends a new
        // reply work item carrying the rejection comment. Invalidate
        // rather than try to patch the cache surgically — the reply
        // arrives from a separate persist and we'd otherwise need a
        // second mutation hook to keep them in sync.
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
            // Rejection reopens the item back into the recipients' queues,
            // raising the caller's outstanding count when they are a recipient.
            void queryClient.invalidateQueries({ queryKey: WORK_ITEMS_MINE_KEY });
        },
    });
}

export function useAttachWorkItemQueue({
    workflowId,
    entityType,
    entityId,
}: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: ({ workItemId, queueId }: { workItemId: string; queueId: string }) =>
            attachWorkItemQueue(client, {
                workflowId,
                workItemId,
                entityType,
                entityId,
                queueId,
            }),
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: ["workitems"] });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

export function useDetachWorkItemQueue({
    workflowId,
    entityType,
    entityId,
}: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: ({ workItemId, queueId }: { workItemId: string; queueId: string }) =>
            detachWorkItemQueue(client, {
                workflowId,
                workItemId,
                entityType,
                entityId,
                queueId,
            }),
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: ["workitems"] });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

export function useReassignWorkItemQueue({
    workflowId,
    entityType,
    entityId,
}: UseAddWorkItemParams) {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const queryKey = workflowKeys.detail(workflowId, entityType, entityId);

    return useMutation({
        mutationFn: async ({
            workItemId,
            currentQueueIds,
            newQueueId,
        }: {
            workItemId: string;
            currentQueueIds: readonly string[];
            newQueueId: string;
        }) => {
            // Attach first: the domain blocks detaching the last visible
            // queue, so the new queue must be in place before any old
            // queues are removed.
            const updated = await attachWorkItemQueue(client, {
                workflowId,
                workItemId,
                entityType,
                entityId,
                queueId: newQueueId,
            });
            for (const queueId of currentQueueIds) {
                if (queueId === newQueueId) continue;
                await detachWorkItemQueue(client, {
                    workflowId,
                    workItemId,
                    entityType,
                    entityId,
                    queueId,
                });
            }
            return updated;
        },
        onSuccess: (updated: WorkItem) => {
            queryClient.setQueryData<Workflow>(queryKey, (prev) => {
                if (prev === undefined) {
                    return undefined;
                }
                return {
                    ...prev,
                    workItems: prev.workItems.map((item) =>
                        item.id === updated.id ? updated : item,
                    ),
                };
            });
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: ["workitems"] });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

export function useCreateWorkflow() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateWorkflowInput) => createWorkflow(client, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: workflowKeys.all });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

/**
 * Ask composer entry point. Posts to `/workflows/ask`, which materializes
 * an ad-hoc workflow with server-resolved entity coordinates and an
 * originating queue (defaults to the asker's personal queue). On success
 * we seed the detail cache so the navigation that follows lands warm,
 * mirroring useSpawnChildWorkflow.
 */
export function useAskQuestion() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: AskQuestionInput) => askQuestion(client, input),
        onSuccess: (created: Workflow) => {
            queryClient.setQueryData(
                workflowKeys.detail(created.id, created.entityType, created.entityId),
                created,
            );
            void queryClient.invalidateQueries({ queryKey: workflowKeys.all });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

/**
 * Spawn a child workflow from an existing work item via the @-mention
 * composer. On success, optimistically seeds the new workflow's detail
 * cache (so a navigation to it lands warm) and invalidates the parent
 * workflow's detail so any header chip / child-count badge re-renders.
 */
export function useSpawnChildWorkflow() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: SpawnChildWorkflowInput) => spawnChildWorkflow(client, input),
        onSuccess: (child: Workflow, input: SpawnChildWorkflowInput) => {
            queryClient.setQueryData(
                workflowKeys.detail(child.id, child.entityType, child.entityId),
                child,
            );
            void queryClient.invalidateQueries({
                queryKey: workflowKeys.detail(
                    input.parentWorkflowId,
                    input.entityType,
                    input.entityId,
                ),
            });
            void queryClient.invalidateQueries({
                queryKey: workflowKeys.children(
                    input.parentWorkflowId,
                    input.parentWorkItemId,
                    input.entityType,
                    input.entityId,
                ),
            });
            void queryClient.invalidateQueries({ queryKey: workflowKeys.all });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}

export interface UseChildWorkflowsParams {
    readonly workflowId: string;
    readonly workItemId: string;
    readonly entityType: string;
    readonly entityId: string;
    /**
     * When false the query is suspended; the consumer is expected to
     * flip this true once it actually needs the list (e.g. a popover
     * opening). Keeps the per-workflow read cheap by default.
     */
    readonly enabled?: boolean;
}

export function useChildWorkflows({
    workflowId,
    workItemId,
    entityType,
    entityId,
    enabled = true,
}: UseChildWorkflowsParams) {
    const client = useApiClient();
    const isEnabled =
        enabled &&
        workflowId.length > 0 &&
        workItemId.length > 0 &&
        entityType.length > 0 &&
        entityId.length > 0;
    const queryKey = workflowKeys.children(workflowId, workItemId, entityType, entityId);
    // Selecting another task keeps the previous thread list on screen,
    // dimmed, until the new one arrives. Consumers that reconcile URL
    // state from this list must skip while `isPlaceholderData` is set.
    const placeholder = useKeepAdjacentData(queryKey, isEnabled);
    return useQuery({
        queryKey,
        queryFn: () => listChildWorkflows(client, workflowId, workItemId, entityType, entityId),
        enabled: isEnabled,
        ...placeholder,
    });
}

export function useSearchWorkflows(query: string, take = 25, skip = 0) {
    const client = useApiClient();
    const trimmedQuery = useMemo(() => query.trim(), [query]);

    return useQuery({
        queryKey: [...workflowKeys.search(trimmedQuery), skip, take] as const,
        queryFn: () => searchWorkflows(client, trimmedQuery, skip, take),
        enabled: trimmedQuery.length >= 2,
        staleTime: 30_000,
    });
}
